import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { formatDate } from '@vaultsfyi/common/utils'
import {
  type ComponentProps,
  type ReactElement,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { Area, AreaChart, Customized, Brush as RechartsBrush, YAxis } from 'recharts'

const BRUSH_TOP_PADDING = 48
const BRUSH_PREVIEW_HEIGHT = 56
const BRUSH_LAYOUT_HEIGHT = BRUSH_TOP_PADDING + BRUSH_PREVIEW_HEIGHT
const BRUSH_HANDLE_WIDTH = 8
const BRUSH_TRAVELER_WIDTH = 0
const BRUSH_LABEL_GAP = 8
const BRUSH_LABEL_ESTIMATED_CHARACTER_WIDTH = 7
const UNIX_SECONDS_THRESHOLD = 100_000_000_000
const BRUSH_HELPER_RIGHT_PADDING = 8
const BRUSH_HELPER_TEXT_Y = 32
const BRUSH_LABEL_TEXT_Y = 42
const BRUSH_LABEL_FONT_SIZE = 12
const BRUSH_HANDLE_HEIGHT = 32
const BRUSH_HANDLE_RADIUS = 4

interface ChartBrushState {
  startIndex?: number
  endIndex?: number
}

interface ChartBrushRangeState {
  range: ChartBrushState
  resetKey?: string
}

type ChartBrushData = Array<{ timestamp?: unknown }>

interface ChartBrushContextValue extends ChartBrushState {
  data: ChartBrushData
}

const ChartBrushContext = createContext<ChartBrushContextValue | null>(null)

const hideDefaultBrushLabel = () => {
  return ''
}

interface ChartBrushTravelerProps {
  x?: number
  y?: number
}

const ChartBrushTraveler = ({ x = 0, y = 0 }: ChartBrushTravelerProps) => {
  const handleX = x - BRUSH_HANDLE_WIDTH / 2
  const handleY = y + BRUSH_TOP_PADDING + (BRUSH_PREVIEW_HEIGHT - BRUSH_HANDLE_HEIGHT) / 2

  return (
    <rect
      x={handleX}
      y={handleY}
      width={BRUSH_HANDLE_WIDTH}
      height={BRUSH_HANDLE_HEIGHT}
      rx={BRUSH_HANDLE_RADIUS}
      fill="rgb(var(--accent))"
    />
  )
}

const chartBrushProps = {
  'aria-label': 'Chart date range',
  className: 'vaults-chart-brush',
  dataKey: 'timestamp',
  fill: 'rgb(var(--bg-surface))',
  height: BRUSH_LAYOUT_HEIGHT,
  padding: { top: BRUSH_TOP_PADDING, right: 0, bottom: 0, left: 0 },
  stroke: 'rgb(var(--accent))',
  tickFormatter: hideDefaultBrushLabel,
  traveller: ChartBrushTraveler,
  travellerWidth: BRUSH_TRAVELER_WIDTH,
}

const getChartBrushResetKey = (data: ChartBrushData) => {
  return `${data.length}:${data[0]?.timestamp ?? ''}:${data.at(-1)?.timestamp ?? ''}`
}

const useChartBrushState = (
  dataLength: number,
  resetKey?: string,
  onRangeChange?: ComponentProps<typeof RechartsBrush>['onChange']
) => {
  const [rangeState, setRangeState] = useState<ChartBrushRangeState>({ range: {}, resetKey })
  const lastIndex = Math.max(dataLength - 1, 0)
  const range = rangeState.resetKey === resetKey ? rangeState.range : {}

  const startIndex = Math.min(Math.max(range.startIndex ?? 0, 0), lastIndex)
  const endIndex = Math.min(Math.max(range.endIndex ?? lastIndex, 0), lastIndex)

  const onChange = useCallback(
    (nextRange: ChartBrushState) => {
      setRangeState({ range: nextRange, resetKey })
      onRangeChange?.(nextRange)
    },
    [onRangeChange, resetKey]
  )

  return useMemo(
    () => ({
      endIndex,
      onChange,
      startIndex,
    }),
    [endIndex, onChange, startIndex]
  )
}

const formatBrushTimestamp = (timestamp: unknown) => {
  const numericTimestamp = Number(timestamp)

  if (!Number.isFinite(numericTimestamp)) {
    return ''
  }

  return formatDate({
    timestamp: numericTimestamp < UNIX_SECONDS_THRESHOLD ? numericTimestamp * SECOND_IN_MILLISECONDS : numericTimestamp,
    showYear: true,
  })
}

interface ChartBrushOverlaySvgProps {
  data: ChartBrushData
  endIndex?: number
  startIndex?: number
  width?: number
}

const ChartBrushOverlaySvg = ({ data, endIndex, startIndex, width = 0 }: ChartBrushOverlaySvgProps) => {
  const lastIndex = data.length - 1
  const helpX = Math.max(width - BRUSH_HELPER_RIGHT_PADDING, 0)

  if (lastIndex < 0 || width <= 0) {
    return null
  }

  const safeStartIndex = Math.min(Math.max(startIndex ?? 0, 0), lastIndex)
  const safeEndIndex = Math.min(Math.max(endIndex ?? lastIndex, 0), lastIndex)
  const midpoint = width / 2
  const startX = getBrushLabelX(safeStartIndex, lastIndex, width)
  const endX = getBrushLabelX(safeEndIndex, lastIndex, width)
  const startLabelGoesRight = startX < midpoint
  const endLabelGoesRight = endX < midpoint
  const startLabel = formatBrushTimestamp(data[safeStartIndex]?.timestamp)
  const endLabel = formatBrushTimestamp(data[safeEndIndex]?.timestamp)
  const showEndLabel = !labelRangesOverlap({
    endTextAnchor: endLabelGoesRight ? 'start' : 'end',
    endX: endX + (endLabelGoesRight ? BRUSH_LABEL_GAP : -BRUSH_LABEL_GAP),
    endText: endLabel,
    startTextAnchor: startLabelGoesRight ? 'start' : 'end',
    startX: startX + (startLabelGoesRight ? BRUSH_LABEL_GAP : -BRUSH_LABEL_GAP),
    startText: startLabel,
  })

  return (
    <g pointerEvents="none">
      <text
        x={helpX}
        y={BRUSH_HELPER_TEXT_Y}
        fill="rgb(var(--body-foreground))"
        textAnchor="end"
        className="font-medium text-xs uppercase"
      >
        Grab to move • Drag edges to zoom
      </text>
      <text
        x={startX + (startLabelGoesRight ? BRUSH_LABEL_GAP : -BRUSH_LABEL_GAP)}
        y={BRUSH_LABEL_TEXT_Y}
        fill="rgb(var(--body-foreground))"
        fontSize={BRUSH_LABEL_FONT_SIZE}
        textAnchor={startLabelGoesRight ? 'start' : 'end'}
      >
        {startLabel}
      </text>
      {showEndLabel && (
        <text
          x={endX + (endLabelGoesRight ? BRUSH_LABEL_GAP : -BRUSH_LABEL_GAP)}
          y={BRUSH_LABEL_TEXT_Y}
          fill="rgb(var(--body-foreground))"
          fontSize={BRUSH_LABEL_FONT_SIZE}
          textAnchor={endLabelGoesRight ? 'start' : 'end'}
        >
          {endLabel}
        </text>
      )}
    </g>
  )
}

interface ChartBrushLayerProps {
  data: ChartBrushData
  endIndex?: number
  startIndex?: number
}

const ChartBrushLayer = (props: ChartBrushLayerProps) => {
  return <Customized component={<ChartBrushOverlaySvg {...props} />} />
}

export const ChartBrushOverlay = () => {
  const brushContext = useContext(ChartBrushContext)

  if (!brushContext) {
    return null
  }

  return <ChartBrushLayer {...brushContext} />
}

interface ChartBrushProps extends Partial<Omit<ComponentProps<typeof RechartsBrush>, 'children' | 'data' | 'ref'>> {
  children: ReactElement
  data: ChartBrushData
}

type ChartBrushComponent = ((props: ChartBrushProps) => JSX.Element) & {
  defaultProps?: Partial<ChartBrushProps>
  displayName?: string
}

export const ChartBrush: ChartBrushComponent = ({ children, data, onChange, ...props }) => {
  const brushState = useChartBrushState(data.length, getChartBrushResetKey(data), onChange)

  return (
    <ChartBrushContext.Provider value={{ data, ...brushState }}>
      <RechartsBrush {...chartBrushProps} {...props} data={data} {...brushState}>
        {children}
      </RechartsBrush>
    </ChartBrushContext.Provider>
  )
}

ChartBrush.displayName = 'Brush'
ChartBrush.defaultProps = chartBrushProps

interface SingleAreaBrushPanoramaProps
  extends Partial<Omit<ComponentProps<typeof AreaChart>, 'children' | 'data' | 'ref'>> {
  color: string
  data?: ChartBrushData
  dataKey: string
  gradientId: string
}

export const SingleAreaBrushPanorama = ({ color, dataKey, gradientId, ...props }: SingleAreaBrushPanoramaProps) => {
  return (
    <AreaChart {...props}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.45} />
          <stop offset="100%" stopColor={color} stopOpacity={0.04} />
        </linearGradient>
      </defs>
      <YAxis hide domain={['auto', 'auto']} />
      <Area
        dataKey={dataKey}
        type="monotone"
        isAnimationActive={false}
        stroke={color}
        strokeWidth={2}
        fill={`url(#${gradientId})`}
      />
      <ChartBrushOverlay />
    </AreaChart>
  )
}

const getBrushLabelX = (index: number, lastIndex: number, width: number) => {
  if (lastIndex <= 0) {
    return 0
  }

  return (index / lastIndex) * width
}

interface LabelRangesOverlapArgs {
  endText: string
  endTextAnchor: 'start' | 'end'
  endX: number
  startText: string
  startTextAnchor: 'start' | 'end'
  startX: number
}

const labelRangesOverlap = ({
  endText,
  endTextAnchor,
  endX,
  startText,
  startTextAnchor,
  startX,
}: LabelRangesOverlapArgs) => {
  const startWidth = startText.length * BRUSH_LABEL_ESTIMATED_CHARACTER_WIDTH
  const endWidth = endText.length * BRUSH_LABEL_ESTIMATED_CHARACTER_WIDTH
  const startRange = startTextAnchor === 'start' ? [startX, startX + startWidth] : [startX - startWidth, startX]
  const endRange = endTextAnchor === 'start' ? [endX, endX + endWidth] : [endX - endWidth, endX]

  return startRange[0] < endRange[1] && endRange[0] < startRange[1]
}
